import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { IconButton, DynamicField } from '@totalsoft/rocket-ui';
import { emptyString } from 'utils/constants';
import { set } from '@totalsoft/change-tracking-react';
import { TableCell, Tooltip } from '@mui/material';
import TextTableCell from 'components/common/table/TextTableCell';
import { SizedTableRow } from 'components/common/table/TableStyles';
import FlexBox from 'components/common/FlexBox';
import StatusIndicator from './StatusIndicator';

const controlTypeMapping = {
  String: 'Text',
  'String[]': 'Text',
  Decimal: 'Numeric',
  Int32: 'Integer',
  int: 'Integer',
  Boolean: 'Checkbox'
};
const toStringAdapter = onChange => value => onChange(value?.toString());

function getValue(parameterItem) {
  if (parameterItem?.valueType?.toLowerCase() === 'boolean') {
    return Boolean(parameterItem?.defaultValue?.toLowerCase() === 'true');
  } else return parameterItem?.defaultValue || emptyString;
}

function getPlaceHolder(parameterItem) {
  if (parameterItem?.defaultValue === null) {
    return 'null';
  } else if (parameterItem?.defaultValue === undefined) {
    return 'undefined';
  } else if (parameterItem?.valueType?.toLowerCase() === 'boolean' && parameterItem?.defaultValue?.toLowerCase() === 'false') {
    return false;
  }
}

function isIndeterminate(parameterItem) {
  return parameterItem?.valueType?.toLowerCase() === 'boolean' && parameterItem?.defaultValue === null;
}

const ParameterItem = ({ parameterItem, parameterLens, status, editing, pending, disabled, onSave, onEdit, onCancel, onDelete }) => {
  const [t] = useTranslation();

  return (
    <SizedTableRow height={70}>
      <TextTableCell content={parameterItem.categoryName} title={parameterItem.description} />
      <TextTableCell content={`${parameterItem.paramCode} - ${parameterItem.description}`}>{parameterItem.paramCode}</TextTableCell>
      <TableCell>
        <Tooltip title={parameterItem.defaultValue} placement='bottom-start'>
          <span>
            <DynamicField
              key={parameterItem.paramCode}
              value={getValue(parameterItem)}
              controlType={controlTypeMapping[parameterItem.valueType] || 'Text'}
              onChange={parameterLens.defaultValue |> set |> toStringAdapter}
              disabled={!editing || pending}
              placeholder={getPlaceHolder(parameterItem)}
              indeterminate={isIndeterminate(parameterItem)}
            />
          </span>
        </Tooltip>
      </TableCell>
      <TableCell>
        <FlexBox gap={0.5} justifyContent='end'>
          {status && <StatusIndicator paramCode={parameterItem.paramCode} status={status} />}
          {!editing ? (
            <IconButton
              type='edit'
              size='small'
              color='primary'
              variant='text'
              tooltip={t('BehaviorParamTypes.Edit')}
              onClick={onEdit}
              disabled={pending || disabled}
            />
          ) : (
            <>
              <IconButton
                type='cancel'
                tooltip={t('BehaviorParamTypes.Cancel')}
                size={'small'}
                color='primary'
                variant='text'
                onClick={onCancel}
                disabled={pending}
              />
              <IconButton
                type='save'
                size={'small'}
                color='primary'
                variant='text'
                tooltip={t('BehaviorParamTypes.Save')}
                onClick={onSave}
                disabled={pending}
              />
              <IconButton
                type='delete'
                tooltip={t('BehaviorParamTypes.Delete')}
                size={'small'}
                color='primary'
                variant='text'
                onClick={onDelete}
                disabled={pending}
              />
            </>
          )}
        </FlexBox>
      </TableCell>
    </SizedTableRow>
  );
};

ParameterItem.propTypes = {
  parameterItem: PropTypes.object.isRequired,
  parameterLens: PropTypes.object.isRequired,
  status: PropTypes.shape({
    code: PropTypes.string.isRequired,
    errorMessage: PropTypes.string
  }),
  editing: PropTypes.bool.isRequired,
  pending: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

export default ParameterItem;
